<template>
    <section class="ds-ex-card ds-ex-form" aria-label="Быстрое создание вакансии">
        <div class="ds-ex-form-row">
            <div class="ds-ex-form-title">
                <div class="ds-ex-form-name">Новая вакансия</div>
                <div class="ds-ex-muted">Одна вакансия — один сотрудник</div>
            </div>
            <div class="ds-ex-form-field">
                <span class="ds-ex-label">Дата</span>
                <div class="ds-ex-date" :class="{ 'is-error': draft.date && draft.dateErrors[draft.date], 'is-disabled': locked }">
                    <AppDateInput :model-value="dateValue" :min-date="minDay" placeholder="Дата" @update:model-value="onDate" />
                </div>
            </div>
            <div class="ds-ex-form-field">
                <span class="ds-ex-label">Начало</span>
                <div class="ds-ex-time"><TimePicker v-model="draft.start" :disabled="locked" /></div>
            </div>
            <div class="ds-ex-form-field">
                <span class="ds-ex-label">Окончание</span>
                <div class="ds-ex-time"><TimePicker v-model="draft.end" :disabled="locked" /></div>
            </div>
            <div class="ds-ex-form-field ds-ex-form-position">
                <span class="ds-ex-label">Должность</span>
                <ExchangeSelect v-model="draft.positionId" label="Должность" placeholder="Выберите должность" searchable search-placeholder="Найти должность" :options="positionOptions" :disabled="locked" />
            </div>
            <div v-if="showExchangeType" class="ds-ex-form-field">
                <span class="ds-ex-label">Биржа</span>
                <div class="ds-glass-segment ds-ex-segment" role="group" aria-label="Тип биржи">
                    <button
                        v-for="type in EXCHANGE_TYPES"
                        :key="type"
                        type="button"
                        class="ds-glass-segment-option"
                        :class="{ 'is-active': draft.exchangeType === type }"
                        :aria-pressed="draft.exchangeType === type"
                        :disabled="locked"
                        @click="draft.exchangeType = type"
                    >{{ EXCHANGE_TYPE_LABELS[type] }}</button>
                </div>
            </div>
            <button v-if="!draft.commentOpen" type="button" class="ds-ex-link-btn" :disabled="locked" @click="draft.commentOpen = true">+ Комментарий</button>
            <div class="ds-ex-spacer"></div>
            <button type="button" class="ds-glass-btn ds-glass-btn-primary ds-ex-submit" :disabled="!canSubmit" @click="submit">
                {{ saving ? 'Создаём…' : submitLabel }}
            </button>
        </div>

        <div v-if="draft.commentOpen" class="ds-ex-form-comment">
            <span class="ds-ex-label">Комментарий</span>
            <input v-model="draft.comment" type="text" class="ds-ex-field ds-ex-input" maxlength="100" placeholder="Например: разгрузка фуры, нужен опыт" :disabled="locked">
        </div>

        <div v-if="!department" class="ds-ex-form-alert" role="alert">Не удалось определить магазин пользователя — создание недоступно.</div>
        <div v-if="pastError" class="ds-ex-form-alert" role="alert">{{ pastError }}</div>

        <div v-if="draft.pending" class="ds-ex-form-uncertain" role="alert">
            <span>Не удалось узнать результат отправки {{ countLabel(draft.pending.dates.length, VACANCY_FORMS) }}. Повторите — дубли не создадутся.</span>
            <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="saving" @click="retryPending">Повторить отправку</button>
            <button type="button" class="ds-ex-link-btn" :disabled="saving" @click="dropPending">Не повторять</button>
        </div>

        <div class="ds-ex-form-copy">
            <button
                type="button"
                role="switch"
                class="ds-ex-switch"
                :class="{ 'is-on': draft.copyOn }"
                :aria-checked="draft.copyOn"
                aria-label="Скопировать на другие даты"
                :disabled="locked"
                @click="toggleCopy"
            ></button>
            <span :class="draft.copyOn ? 'ds-ex-copy-title' : 'ds-ex-muted'">Скопировать на другие даты</span>
            <template v-if="draft.copyOn">
                <span
                    v-for="day in draft.copyDates"
                    :key="day"
                    class="ds-ex-date-pill"
                    :class="{ 'is-error': draft.dateErrors[day] }"
                    :title="draft.dateErrors[day] || undefined"
                >
                    {{ rowDateLabel(day, '') }}
                    <button type="button" :aria-label="`Убрать ${day}`" :disabled="locked" @click="removeCopyDate(day)">
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    </button>
                </span>
                <VacancyCopyDates v-model="draft.copyDates" :main-date="draft.date" :caption="copyCaption" :time-label="copyTimeLabel" :disabled="locked" />
                <span class="ds-ex-muted">Те же время, должность и биржа. На каждую дату — отдельная вакансия.</span>
            </template>
        </div>
        <ul v-if="errorList.length" class="ds-ex-form-errors">
            <li v-for="item in errorList" :key="item.date"><b>{{ rowDateLabel(item.date, '') }}</b> — {{ item.message }}</li>
        </ul>
    </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import AppDateInput from '@/ui/AppDateInput.vue'
import TimePicker from '@/components/TimePicker.vue'
import { useCalendarToday } from '@/ui/calendarTime'
import { useAuthStore } from '@/stores/authStore'
import ExchangeSelect from './ExchangeSelect.vue'
import VacancyCopyDates from './VacancyCopyDates.vue'
import { exchangeApi } from './exchangeApi'
import { exchangeToast } from './exchangeToasts'
import { errorText } from './exchangeActions'
import { useExchangeDirectory } from './exchangeDirectory'
import { useExchangePage } from './exchangePage'
import {
    buildTimestamp,
    countLabel,
    EXCHANGE_TYPE_LABELS,
    exchangeFlags,
    instantOf,
    rowDateLabel,
    VACANCY_FORMS,
    wallNow,
} from './exchangeFormat'
import type { ExchangeError, ExchangeType } from './exchangeTypes'

const emit = defineEmits<{ created: [ids: string[]] }>()

const EXCHANGE_TYPES: ExchangeType[] = ['internal', 'external', 'both']

const page = useExchangePage()
const draft = page.vacancyDraft
const authStore = useAuthStore()
const directory = useExchangeDirectory()
const today = useCalendarToday()

const saving = ref(false)
const pastError = ref('')

const department = computed(() => directory.profileDepartment.value)
const showExchangeType = computed(() => Boolean(authStore.settings?.USE_EXCHANGE_PORTAL))
const positionOptions = computed(() => directory.positions.value.map(item => ({ id: item.id, name: item.name })))
const position = computed(() => directory.positions.value.find(item => item.id === draft.positionId) ?? null)
const minDay = computed(() => today.value)
const dateValue = computed(() => draft.date ? dayjs(draft.date) : null)
// Пока исход пакета неизвестен, условия не меняются: повтор идёт с тем же ключом и payload.
const locked = computed(() => saving.value || Boolean(draft.pending))

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const timesValid = computed(() => Boolean(draft.start && draft.end && TIME.test(draft.start) && TIME.test(draft.end) && draft.start < draft.end))
const allDates = computed(() => {
    if (!draft.date) return []
    const copies = draft.copyOn ? draft.copyDates.filter(day => day !== draft.date) : []
    return [draft.date, ...new Set(copies)]
})
const canSubmit = computed(() => Boolean(
    !locked.value && department.value && draft.date && timesValid.value && draft.positionId,
))
const submitLabel = computed(() => allDates.value.length > 1 ? `Создать ${countLabel(allDates.value.length, VACANCY_FORMS)}` : 'Создать')
const copyTimeLabel = computed(() => timesValid.value ? `${draft.start!.slice(0, 2)}-${draft.end!.slice(0, 2)}` : '')
const copyCaption = computed(() => [position.value?.name, timesValid.value ? `${draft.start}–${draft.end}` : ''].filter(Boolean).join(' · '))
const errorList = computed(() => Object.entries(draft.dateErrors).map(([date, message]) => ({ date, message })).sort((a, b) => a.date.localeCompare(b.date)))

// Основная дата не дублируется среди дат копий.
function onDate(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    const next = dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : null
    draft.date = next
    draft.copyDates = draft.copyDates.filter(day => day !== next)
}

watch(() => [draft.date, draft.start, draft.end, draft.copyDates.join(',')], () => { pastError.value = '' })

function toggleCopy() {
    draft.copyOn = !draft.copyOn
}

function removeCopyDate(day: string) {
    draft.copyDates = draft.copyDates.filter(item => item !== day)
    const { [day]: _removed, ...rest } = draft.dateErrors
    draft.dateErrors = rest
}

function summaryText(dates: string[]) {
    const time = `${draft.start}–${draft.end}`
    if (dates.length === 1) return `${position.value?.name ?? ''} · ${rowDateLabel(dates[0]).toLowerCase()} · ${time}`
    return `${position.value?.name ?? ''} · ${time}`
}

// Полный успех: дата и комментарий очищаются; время, должность и биржа остаются до ухода из раздела.
function resetAfterSuccess() {
    draft.date = null
    draft.comment = ''
    draft.commentOpen = false
    draft.copyOn = false
    draft.copyDates = []
    draft.dateErrors = {}
    draft.pending = null
}

function newKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    // Запасной UUID v4 для окружений без randomUUID.
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function checkPast(dates: string[]): boolean {
    const now = wallNow()
    const past = dates.filter(day => instantOf(day, draft.start!) < now)
    if (!past.length) return true
    pastError.value = past.length === 1 && past[0] === draft.date
        ? 'Дата и время начала вакансии не могут быть раньше текущего времени'
        : `Начало уже прошло: ${past.map(day => rowDateLabel(day, '')).join(', ')}`
    return false
}

// target-contract §1: не больше 100 дат в одном пакете (иначе 400 dates_limit_exceeded).
const MAX_BATCH_DATES = 100

async function submit() {
    if (!canSubmit.value || !department.value) return
    const dates = allDates.value
    if (dates.length > MAX_BATCH_DATES) {
        pastError.value = `Не больше ${MAX_BATCH_DATES} дат в одном пакете — выбрано ${dates.length}`
        return
    }
    if (!checkPast(dates)) return
    draft.dateErrors = {}
    if (dates.length === 1) await createOne(dates[0])
    else await createBatch(dates)
}

async function createOne(date: string) {
    const payload: Record<string, unknown> = {
        date,
        dttm_work_start_at: buildTimestamp(date, draft.start!),
        dttm_work_end_at: buildTimestamp(date, draft.end!),
        department: department.value!.id,
        position: draft.positionId,
        ...exchangeFlags(showExchangeType.value ? draft.exchangeType : 'internal'),
    }
    if (draft.comment.trim()) payload.comment = draft.comment.trim()
    saving.value = true
    try {
        const created = await exchangeApi.createOne(payload)
        exchangeToast.success('Вакансия опубликована', { text: summaryText([date]), key: 'create' })
        resetAfterSuccess()
        emit('created', created?.id ? [created.id] : [])
    } catch (error) {
        const e = error as ExchangeError
        if (e?.kind === 'network') {
            // У одиночного POST нет ключа повтора: исход неизвестен, список обновляется, автоповтора нет.
            exchangeToast.error('Не удалось узнать результат', { text: 'Проверьте список — данные обновлены', key: 'create' })
            emit('created', [])
        } else {
            exchangeToast.error('Вакансия не создана', { text: errorText(error, 'Попробуйте ещё раз'), key: 'create' })
        }
    } finally {
        saving.value = false
    }
}

async function createBatch(dates: string[]) {
    const payload: Record<string, unknown> = {
        department: department.value!.id,
        position: draft.positionId,
        dates,
        work_start_time: draft.start,
        work_end_time: draft.end,
        ...exchangeFlags(showExchangeType.value ? draft.exchangeType : 'internal'),
        comment: draft.comment.trim() || null,
    }
    // Новый осознанный пакет — новый ключ, даже при тех же условиях.
    draft.pending = { key: newKey(), payload, dates }
    await sendPending()
}

async function sendPending() {
    const pending = draft.pending
    if (!pending) return
    saving.value = true
    try {
        const result = await exchangeApi.createBatch(pending.payload, pending.key)
        draft.pending = null
        applyBatchResult(result.results, pending.dates)
    } catch (error) {
        const e = error as ExchangeError
        if (e?.kind === 'network') {
            // Неопределённый исход не равен отказу: ключ и payload сохранены для безопасного повтора.
            exchangeToast.error('Не удалось узнать результат', { text: 'Повторите отправку — дубли не создадутся', key: 'create' })
            emit('created', [])
        } else {
            draft.pending = null
            exchangeToast.error('Вакансии не созданы', { text: errorText(error, 'Попробуйте ещё раз'), key: 'create' })
        }
    } finally {
        saving.value = false
    }
}

function applyBatchResult(results: Array<{ date: string, status: string, vacancy_id: string | null, error: { message: string } | null }>, dates: string[]) {
    const created = results.filter(item => item.status === 'created')
    const failed = results.filter(item => item.status !== 'created')
    const ids = created.map(item => item.vacancy_id).filter((id): id is string => Boolean(id))
    if (!failed.length) {
        exchangeToast.success(`Опубликовано ${countLabel(created.length, VACANCY_FORMS)}`, { text: summaryText(dates), key: 'create' })
        resetAfterSuccess()
        emit('created', ids)
        return
    }
    // Частичный результат: успешные даты исключены, неудачные и все значения остаются для исправления.
    const failedDates = failed.map(item => item.date)
    draft.dateErrors = Object.fromEntries(failed.map(item => [item.date, item.error?.message ?? 'Не создана']))
    draft.date = failedDates[0]
    draft.copyDates = failedDates.slice(1)
    draft.copyOn = failedDates.length > 1
    const title = created.length ? `Созданы ${created.length} из ${results.length}` : 'Вакансии не созданы'
    const text = `Не созданы: ${failedDates.map(day => rowDateLabel(day, '')).join(', ')}`
    if (created.length) exchangeToast.success(title, { text, key: 'create' })
    else exchangeToast.error(title, { text, key: 'create' })
    emit('created', ids)
}

function retryPending() {
    void sendPending()
}

// Отказ от повтора: пользователь сам проверяет список; ключ сбрасывается.
function dropPending() {
    draft.pending = null
    emit('created', [])
}

</script>
